import React, { useEffect, useMemo } from 'react';
import { useAiRovoCurrentRouteConfiguration } from '@jira/ai-rovo_actions-store/src/useAiRovoCurrentRouteConfiguration.tsx';
import { useOrgId } from '@jira/router-resources__navigation-organization-id/src/useOrgId.tsx';
import { useConversationAssistantSidebarApi } from '@jira/platform__conversation-assistant/src/useConversationAssistantSidebarApi.tsx';
import { useJiraIssueCommentEditor } from '@jira/platform__conversation-assistant/src/useJiraIssueCommentEditor.tsx';
import { useRovoSubproductAttribution } from '@jira/platform__conversation-assistant/src/useRovoSubproductAttribution.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import type { MentionProvider } from '@atlaskit/mention/types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api/ai-smart-button-store';
import { AISmartButton } from '@atlassian/ai-smart-button/ai-smart-button';
import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

const noopMentionProvider: MentionProvider = {
	filter: () => Promise.resolve([]),
	isFiltering: () => false,
	recordMentionSelection: () => undefined,
	shouldHighlightMention: () => false,
	subscribe: () => undefined,
	unsubscribe: () => undefined,
};

const noopJiraIssueCommentEditor: NonNullable<
	ConversationAssistantConfig['jiraIssueCommentEditor']
> = {
	createMentionProvider: () => Promise.resolve(noopMentionProvider),
	onCommentSave: () => Promise.resolve(),
};

const useConditionalJiraIssueCommentEditor = functionWithCondition(
	() => fg('agent_session_experience_fg'),
	useJiraIssueCommentEditor,
	() => noopJiraIssueCommentEditor,
);

export const AiRovoButtonWrapperRefresh = (): React.JSX.Element => {
	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();
	const configuration = useAiRovoCurrentRouteConfiguration();
	const sidebarApi = useConversationAssistantSidebarApi({});
	const jiraIssueCommentEditor = useConditionalJiraIssueCommentEditor();
	const attributionProduct = useRovoSubproductAttribution();
	const { setNudges } = useAISmartButtonActions();
	const uiConfig = useMemo(
		() => ({
			...configuration.uiConfig,
			elements: {
				...configuration.uiConfig?.elements,
				voiceDictationButton: true,
			},
			isContextPanelEnabled: expVal('ai_agent_sessions_experience', 'isEnabled', false),
		}),
		[configuration.uiConfig],
	);

	useEffect(() => {
		setNudges(configuration.nudges ?? []);
	}, [configuration.nudges, setNudges]);

	// Match to logic in platform/packages/insights-ai-platform/ai-smart-button/src/ui/ai-mini-modal/main.tsx
	// and jira/src/packages/ai-rovo/button/src/AiRovoButtonWrapperRefresh.tsx
	const conversationAssistantConfig = useMemo(
		(): ConversationAssistantConfig | undefined =>
			expVal('prevent_abort_jira_rovo_chat', 'isEnabled', false)
				? {
						displaySubmitFlag: true,
						siteId: cloudId,
						// Always 'jira' so platform UI checks (for example full-screen modal and SLA actions)
						// that gate on product === 'jira' keep existing behavior on JSM/JPD routes.
						product: 'jira',
						orgId,
						assistanceServiceConfig: {
							headers: {
								'x-issynthetic-traffic': 'false',
								'X-Cloudid': cloudId,
								// Override the default x-product header (which otherwise mirrors `product`)
								// so assist-service attribution reflects the actual subproduct.
								...(attributionProduct !== 'jira' && fg('jira_rovo_subproduct_attribution_header')
									? { 'x-product': attributionProduct }
									: {}),
							},
							baseUrl: '/gateway/api/assist',
						},
						preventAbortRequestsOnStagingModalClose: true,
						preventAbortRequestsOnStandaloneChatClose: true,
						...(fg('agent_session_experience_fg') ? { jiraIssueCommentEditor } : {}),
					}
				: undefined,
		[cloudId, orgId, jiraIssueCommentEditor, attributionProduct],
	);

	return (
		<AISmartButton
			product="jira"
			cloudId={cloudId}
			orgId={orgId}
			isNav4Enabled
			sidebarApi={sidebarApi}
			productPage="jira"
			getActions={configuration.getActions}
			tags={configuration.tags}
			uiConfig={uiConfig}
			prioritizeCustomActionResults={configuration.prioritizeCustomActionResults}
			conversationAssistantConfig={conversationAssistantConfig}
		/>
	);
};
